import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogOut, Eye, EyeOff, Copy, Check, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCountUp } from "@/hooks/useCountUp";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_aa4f8546-5d46-42d2-8104-f84919deefe1/artifacts/prtdjqf5_NWP%20white.png";

const MONTH_DE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function germanMonthLabel(monthStr) {
  const [y, m] = String(monthStr).split("-").map(Number);
  if (!y || !m) return monthStr;
  return `${MONTH_DE[m - 1]} '${String(y).slice(-2)}`;
}

function KPI({ label, value, suffix, testid, color = "#fff" }) {
  return (
    <div
      data-testid={testid}
      className="nwp-card nwp-card-hover nwp-fade-up p-5 sm:p-6"
    >
      <div className="nwp-eyebrow mb-3">{label}</div>
      <div
        className="nwp-big-number text-2xl sm:text-3xl nwp-numeric"
        style={{ color }}
      >
        {value}
        {suffix ? <span className="ml-1 text-base" style={{ color: "var(--nwp-text-muted)" }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

function CountUpCurrency({ value }) {
  const animated = useCountUp(value || 0, 800);
  return <span className="nwp-numeric">{formatCurrency(animated)}</span>;
}

function CountUpInt({ value }) {
  const animated = useCountUp(value || 0, 800);
  return <span className="nwp-numeric">{Math.round(animated).toLocaleString("de-DE")}</span>;
}

function BarTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const v = payload[0]?.value ?? 0;
  return (
    <div
      data-testid="admin-bar-tooltip"
      style={{
        background: "rgba(15,15,15,0.96)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 10,
        padding: "8px 12px",
        color: "#fff",
        fontSize: 12,
      }}
    >
      <div style={{ color: "#cfcfcf", fontSize: 11 }}>{label}</div>
      <div className="nwp-numeric">{v} Berechnungen</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [newPwError, setNewPwError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Statistik konnte nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try { await api.post("/admin/logout"); } catch (_) {}
    navigate("/admin/login", { replace: true });
  };

  const handleCopyPw = async () => {
    if (!stats?.current_user_password) return;
    try {
      await navigator.clipboard.writeText(stats.current_user_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const handleSavePw = async (e) => {
    e.preventDefault();
    if (!newPw || newPw.length < 4) {
      setNewPwError("Mindestens 4 Zeichen erforderlich");
      return;
    }
    setNewPwError("");
    setSaving(true);
    try {
      await api.patch("/admin/user-password", { new_password: newPw });
      toast.success("Passwort aktualisiert — bestehende Sessions wurden zurückgesetzt");
      setNewPw("");
      await fetchStats();
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      } else {
        const msg = e?.response?.data?.detail;
        setNewPwError(typeof msg === "string" ? msg : "Speichern fehlgeschlagen");
      }
    } finally {
      setSaving(false);
    }
  };

  const chartData = useMemo(() => {
    if (!stats?.monthly_counts) return [];
    return stats.monthly_counts.map((b) => ({
      label: germanMonthLabel(b.month),
      month: b.month,
      count: b.count,
    }));
  }, [stats]);

  const reOn = stats?.reinvest_distribution?.on ?? 0;
  const reOff = stats?.reinvest_distribution?.off ?? 0;
  const addonWith = stats?.weekly_addon_usage?.with_addon ?? 0;
  const addonWithout = stats?.weekly_addon_usage?.without_addon ?? 0;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 flex items-center justify-between px-5 py-4 md:px-10"
        data-testid="admin-topbar"
        style={{
          background: "rgba(10,10,10,0.75)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--nwp-border)",
        }}
      >
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="NWP" className="h-9 w-auto" data-testid="nwp-logo" />
          <span
            data-testid="admin-badge"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold"
            style={{
              background: "rgba(225,6,0,0.12)",
              border: "1px solid rgba(225,6,0,0.35)",
              color: "#ff5a55",
              letterSpacing: "0.18em",
            }}
          >
            <ShieldAlert size={11} />
            <span>ADMIN</span>
          </span>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-button"
          className="nwp-btn-ghost text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
        >
          <LogOut size={13} />
          <span>Abmelden</span>
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10 md:px-10 md:py-14">
        <div className="nwp-fade-up mb-10">
          <div className="nwp-eyebrow mb-2" style={{ color: "var(--nwp-gold)" }}>
            Verwaltung
          </div>
          <h1
            data-testid="admin-page-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-light"
            style={{ letterSpacing: "-0.02em", color: "#fff" }}
          >
            Admin Dashboard
          </h1>
          <p className="text-sm mt-3" style={{ color: "var(--nwp-text-muted)" }}>
            Passwort verwalten und Berechnungs-Statistik einsehen
          </p>
        </div>

        {/* Password management */}
        <section
          data-testid="password-management-section"
          className="nwp-card nwp-fade-up p-6 md:p-8 mb-10"
          style={{ animationDelay: "60ms" }}
        >
          <div className="nwp-eyebrow mb-2">User-Gate</div>
          <h2 className="text-xl font-medium mb-5" style={{ color: "#fff" }}>
            Passwort verwalten
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current password */}
            <div>
              <div className="text-xs uppercase mb-2" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
                Aktuelles Passwort
              </div>
              <div
                className="flex items-center gap-2 rounded-lg px-3 py-2"
                style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.10)" }}
              >
                <span
                  data-testid="current-password-display"
                  className="flex-1 nwp-numeric"
                  style={{ color: "#fff", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                >
                  {loading
                    ? "…"
                    : showPw
                    ? stats?.current_user_password ?? ""
                    : "•".repeat(Math.max(8, (stats?.current_user_password ?? "").length))}
                </span>
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPw((s) => !s)}
                  className="p-2 rounded-md hover:bg-white/5 transition"
                  style={{ color: "#cfcfcf" }}
                  aria-label={showPw ? "Verbergen" : "Anzeigen"}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                  type="button"
                  data-testid="copy-password-button"
                  onClick={handleCopyPw}
                  className="p-2 rounded-md hover:bg-white/5 transition"
                  style={{ color: copied ? "#5af5a8" : "#cfcfcf" }}
                  aria-label="Kopieren"
                >
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>
              <div className="text-[11px] mt-2" style={{ color: "var(--nwp-text-muted)" }}>
                Beim Ändern werden bestehende Sessions automatisch zurückgesetzt.
              </div>
            </div>

            {/* New password form */}
            <form onSubmit={handleSavePw} data-testid="change-password-form" className="flex flex-col">
              <div className="text-xs uppercase mb-2" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
                Neues Passwort
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  data-testid="new-password-input"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Mindestens 4 Zeichen"
                  className="nwp-input h-11 text-base"
                />
                <button
                  type="submit"
                  data-testid="save-password-button"
                  disabled={saving || newPw.length < 4}
                  className="nwp-btn-primary rounded-xl font-medium px-5"
                  style={{ height: 44, fontSize: 14, minWidth: 110 }}
                >
                  {saving ? "Speichern…" : "Speichern"}
                </button>
              </div>
              {newPwError && (
                <div data-testid="new-password-error" className="text-xs mt-2" style={{ color: "#ff5a55" }}>
                  {newPwError}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* KPI cards */}
        <section data-testid="kpi-section" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPI
            label="Berechnungen insgesamt"
            testid="kpi-total"
            value={loading ? "…" : <CountUpInt value={stats?.total_calculations ?? 0} />}
          />
          <KPI
            label="Ø Erstinvest"
            testid="kpi-avg-initial"
            value={loading ? "…" : <CountUpCurrency value={stats?.avg_initial_invest ?? 0} />}
          />
          <KPI
            label="Reinvest-Anteil"
            testid="kpi-reinvest"
            value={
              loading ? (
                "…"
              ) : (
                <span style={{ color: "#ff5a55" }}>
                  {reOn} %
                  <span className="text-base ml-1" style={{ color: "var(--nwp-text-muted)" }}>
                    An / {reOff} % Aus
                  </span>
                </span>
              )
            }
          />
        </section>

        {/* Monthly bar chart */}
        <section
          data-testid="monthly-chart-section"
          className="nwp-card nwp-fade-up p-6 md:p-8 mb-6"
          style={{ animationDelay: "120ms" }}
        >
          <div className="nwp-eyebrow mb-2">Statistik</div>
          <h3 className="text-lg sm:text-xl font-medium mb-5" style={{ color: "#fff" }}>
            Berechnungen pro Monat
          </h3>
          <div data-testid="monthly-chart" style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3a35" stopOpacity={0.95} />
                    <stop offset="100%" stopColor="#7a0300" stopOpacity={0.85} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#999", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#999", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(225,6,0,0.06)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={42}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill="url(#barGradient)" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Secondary row */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="nwp-card nwp-fade-up p-6" data-testid="addon-usage-card" style={{ animationDelay: "180ms" }}>
            <div className="nwp-eyebrow mb-2">Zusatzinvest-Nutzung</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-2xl nwp-numeric" style={{ color: "var(--nwp-gold)" }}>
                {loading ? "…" : `${addonWith} %`}
              </span>
              <span className="text-xs" style={{ color: "var(--nwp-text-muted)" }}>mit Zusatzinvest</span>
            </div>
            <div
              className="w-full overflow-hidden rounded-full"
              style={{ height: 10, background: "rgba(255,255,255,0.06)" }}
            >
              <div
                style={{
                  width: `${addonWith}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #d4af37 0%, #f0d27a 100%)",
                  transition: "width 600ms cubic-bezier(0.2, 0.7, 0.3, 1)",
                }}
              />
            </div>
            <div className="flex justify-between text-[11px] mt-2" style={{ color: "var(--nwp-text-muted)" }}>
              <span>mit: {addonWith} %</span>
              <span>ohne: {addonWithout} %</span>
            </div>
          </div>

          <div className="nwp-card nwp-fade-up p-6" data-testid="reinvest-distribution-card" style={{ animationDelay: "220ms" }}>
            <div className="nwp-eyebrow mb-2">Reinvest-Verteilung</div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-2xl nwp-numeric" style={{ color: "#ff5a55" }}>
                {loading ? "…" : `${reOn} %`}
              </span>
              <span className="text-xs" style={{ color: "var(--nwp-text-muted)" }}>Reinvest aktiv</span>
            </div>
            <div
              className="w-full overflow-hidden rounded-full flex"
              style={{ height: 10, background: "rgba(255,255,255,0.06)" }}
            >
              <div
                style={{
                  width: `${reOn}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #e10600 0%, #ff3a35 100%)",
                  transition: "width 600ms cubic-bezier(0.2, 0.7, 0.3, 1)",
                }}
              />
              <div
                style={{
                  width: `${reOff}%`,
                  height: "100%",
                  background: "rgba(255,255,255,0.10)",
                  transition: "width 600ms cubic-bezier(0.2, 0.7, 0.3, 1)",
                }}
              />
            </div>
            <div className="flex justify-between text-[11px] mt-2" style={{ color: "var(--nwp-text-muted)" }}>
              <span>An: {reOn} %</span>
              <span>Aus: {reOff} %</span>
            </div>
          </div>
        </section>

        <footer className="mt-10 pt-6" style={{ borderTop: "1px solid var(--nwp-border-soft)" }}>
          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 text-[11px]" style={{ color: "#6b6b6b" }}>
            <div>© NWP — Admin Panel</div>
            <div style={{ letterSpacing: "0.14em", color: "#4a4a4a" }}>DUBAI STOCK EXCHANGE · SECURED TRADING</div>
          </div>
        </footer>
      </main>
    </div>
  );
}
